/**
 * Profile Questionnaire constants.
 *
 * These constants are the single source of truth for the canonical
 * MVP options lists referenced by the questionnaire and the profile
 * detail view.
 *
 * Authority:
 * - CITY_MVP           — partner PC-015 (Москва, Санкт-Петербург)
 * - EDUCATION_TYPES    — partner PC-045 (Среднее, СПО, Незаконченное
 *                        высшее, Высшее, К/н, Д/н)
 * - WORK_EDUCATION_SPHERES — DEMO / NON-AUTHORITATIVE per PC-043 and
 *                        PC-044. Marked explicitly.
 *
 * Field-length limits are mirrored from `frontend/src/api/profiles.ts`
 * (`MAX_DISPLAY_NAME`, `MAX_HEADLINE`, `MAX_BIO`, `MAX_CITY_OR_REGION`)
 * and from `internal/profiles/...` `MinAge`/`MaxAge`. Backend remains
 * authoritative; we mirror here for client-side responsiveness.
 */

import { MIN_AGE, MAX_AGE } from '@/api/profiles';

/** MVP city list per partner correction PC-015. */
export const CITY_MVP: readonly string[] = Object.freeze(['Москва', 'Санкт-Петербург']);

/**
 * Education type dropdown per partner PC-045.
 * Rendered in this order; each value is the canonical Russian label.
 */
export const EDUCATION_TYPES: readonly string[] = Object.freeze([
  'Среднее',
  'СПО',
  'Незаконченное высшее',
  'Высшее',
  'К/н',
  'Д/н',
]);

/**
 * Work/education sphere dictionary.
 *
 * IMPORTANT: This is a **placeholder dictionary** used to populate the
 * form controls in this ACT. It is DEMO / NON-AUTHORITATIVE per partner
 * correction PC-043 ("Any demo taxonomy shipped before freeze MUST be
 * labelled non-authoritative in code and docs") and per
 * `internal/profiles/activation.go` ("work_sphere: future field").
 *
 * The list below is intentionally small, covers common spheres, and
 * is the single shared source for BOTH `WorkSphereField` and
 * `EducationSphereField` per partner PC-044.
 *
 * Final taxonomy is OPEN-PRODUCT-005 and remains a partner-owned
 * decision. Do not expand this list in this ACT.
 */
export const WORK_EDUCATION_SPHERES: readonly string[] = Object.freeze([
  'IT и технологии',
  'Финансы и инвестиции',
  'Медицина и здоровье',
  'Образование и наука',
  'Творчество и искусство',
  'Бизнес и предпринимательство',
  'Право и государство',
  'Маркетинг и медиа',
  'Сервис и гостеприимство',
  'Строительство и инженерия',
  'Другое',
]);

/** Maximum display name length, mirrored from backend. */
export const MAX_DISPLAY_NAME = 100;

/** Maximum headline length, mirrored from backend. */
export const MAX_HEADLINE = 200;

/** Maximum bio length, mirrored from backend. */
export const MAX_BIO = 1000;

/** Maximum city_or_region length, mirrored from backend. */
export const MAX_CITY_OR_REGION = 100;

/** Minimum age per REQ-125. */
export const MIN_AGE_LOCAL = MIN_AGE;

/** Maximum age per REQ-125. */
export const MAX_AGE_LOCAL = MAX_AGE;

/**
 * The list of backend field codes that are required for activation.
 * These come from `internal/profiles/activation.go` /
 * `GetMissingActivationFields`; we mirror here to know when the
 * questionnaire can claim local completeness (UI hint only — backend
 * remains authoritative).
 */
export const CANONICAL_REQUIRED_FIELDS: readonly string[] = Object.freeze([
  'gender',
  'goal',
  'city',
  'display_name',
  'headline_or_bio',
]);

/**
 * Russian labels for missing-activation-field codes. Mirrors
 * `frontend/src/features/friend-profile/model.ts` `fieldCodeToLabel`
 * (which exists in English) and extends it for the questionnaire UX.
 *
 * Used by the questionnaire view to render the missing-fields list
 * returned by the backend.
 */
export const FIELD_CODE_LABEL_RU: Readonly<Record<string, string>> = Object.freeze({
  gender: 'Пол',
  goal: 'Сценарий',
  age: 'Возраст',
  city: 'Город',
  display_name: 'Имя',
  headline: 'Заголовок',
  bio: 'О себе',
  headline_or_bio: 'Заголовок или о себе',
});

/** Russian label for "Female" / "Male" gender selection inside the form. */
export const GENDER_OPTIONS: ReadonlyArray<{ value: 'male' | 'female'; label: string }> =
  Object.freeze([
    { value: 'female', label: 'Женщина' },
    { value: 'male', label: 'Мужчина' },
  ]);